/**
 * EngageTrack React Context
 */

import React, { createContext, useContext, useEffect, useRef, ReactNode } from 'react';
import type { EngageTrackConfig, EngageTrackContextValue, SessionState } from '../types';
import { EngageTrackService } from '../core/EngageTrackService';

const EngageTrackContext = createContext<EngageTrackContextValue | null>(null);

export interface EngageTrackProviderProps {
  config: EngageTrackConfig;
  children: ReactNode;
}

/**
 * EngageTrack Provider Component
 * Wraps your app to provide tracking functionality throughout the component tree
 */
export const EngageTrackProvider: React.FC<EngageTrackProviderProps> = ({ config, children }) => {
  const serviceRef = useRef<EngageTrackService | null>(null);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    // Initialize service on mount
    if (!serviceRef.current) {
      serviceRef.current = new EngageTrackService(config);
      serviceRef.current.initialize();
      isInitializedRef.current = true;
    }

    // Cleanup on unmount
    return () => {
      if (serviceRef.current) {
        serviceRef.current.cleanup();
        serviceRef.current = null;
        isInitializedRef.current = false;
      }
    };
  }, [config]);

  const track = (eventType: string, data?: Record<string, unknown>) => {
    if (serviceRef.current) {
      serviceRef.current.track(eventType, data);
    }
  };

  const trackReferralConversion = (data?: Record<string, unknown>) => {
    if (serviceRef.current) {
      serviceRef.current.trackReferralConversion(data);
    }
  };

  const getSessionState = (): SessionState => {
    if (serviceRef.current) {
      return serviceRef.current.getSessionState();
    }
    return {
      sessionId: null,
      userId: null,
      dbSessionId: null,
      startTime: 0,
      lastActivityTime: 0,
      pageViewCount: 0,
      isActive: false,
      isConnected: false,
    };
  };

  const value: EngageTrackContextValue = {
    config,
    isInitialized: isInitializedRef.current,
    track,
    trackReferralConversion,
    getSessionState,
  };

  return <EngageTrackContext.Provider value={value}>{children}</EngageTrackContext.Provider>;
};

/**
 * Hook to access EngageTrack context
 * @throws Error if used outside of EngageTrackProvider
 */
export const useEngageTrackContext = (): EngageTrackContextValue => {
  const context = useContext(EngageTrackContext);
  if (!context) {
    throw new Error('useEngageTrackContext must be used within EngageTrackProvider');
  }
  return context;
};
